import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AdminPageHeader, RowActions } from "@/components/admin/crud-helpers";
import {
  upsertAccount, deleteAccount,
  createJournalEntry, voidJournalEntry,
  upsertExpenseCategory,
  upsertExpense, setExpenseStatus, deleteExpense,
  upsertBooking, setBookingStatus, deleteBooking,
} from "@/lib/erp.functions";
import { Plus, Trash2, BookOpen, Receipt, Calendar, BarChart3, TrendingUp, TrendingDown, Wallet, Landmark, FileText, DollarSign } from "lucide-react";
import { StatsStrip } from "@/components/admin/stats-strip";

export const Route = createFileRoute("/_authenticated/admin/erp")({
  component: ErpPage,
});

type AccountType = "asset" | "liability" | "equity" | "revenue" | "expense";
type Account = {
  id: string; code: string; name: string; type: AccountType;
  parent_id: string | null; currency: string; description: string | null; active: boolean;
};
type Category = { id: string; name: string; slug: string; account_id: string | null; color: string; active: boolean };
type Expense = {
  id: string; expense_date: string; category_id: string | null; vendor: string | null;
  description: string | null; amount_cents: number; tax_cents: number; currency: string;
  payment_method: string | null; status: "draft"|"submitted"|"approved"|"paid"|"rejected";
  receipt_url: string | null; notes: string | null;
};
type Booking = {
  id: string; reference: string; resource_name: string; resource_type: string | null;
  customer_name: string; customer_email: string | null; customer_phone: string | null;
  start_at: string; end_at: string; guests: number; total_cents: number; currency: string;
  status: "pending"|"confirmed"|"checked_in"|"checked_out"|"cancelled"|"no_show"; notes: string | null;
};
type Entry = {
  id: string; entry_number: string; entry_date: string; description: string | null;
  reference: string | null; status: "draft"|"posted"|"void"; total_cents: number; currency: string;
};
type Line = { id: string; entry_id: string; account_id: string; debit_cents: number; credit_cents: number; memo: string | null };

const fmtMoney = (cents: number, cur = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format((cents ?? 0) / 100);

const ACCOUNT_TYPES: { value: AccountType; label: string }[] = [
  { value: "asset", label: "Asset" }, { value: "liability", label: "Liability" },
  { value: "equity", label: "Equity" }, { value: "revenue", label: "Revenue" }, { value: "expense", label: "Expense" },
];

function ErpPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">ERP</h1>
        <p className="text-sm text-muted-foreground">
          Chart of accounts, journal entries, expenses, bookings and reports.
        </p>
      </div>
      <Tabs defaultValue="reports">
        <PillTabsList search className="flex flex-wrap">
          <PillTabsTrigger value="reports"><BarChart3 className="mr-1.5 h-4 w-4" />Reports</PillTabsTrigger>
          <PillTabsTrigger value="accounts"><BookOpen className="mr-1.5 h-4 w-4" />Chart of accounts</PillTabsTrigger>
          <PillTabsTrigger value="ledger">Ledger</PillTabsTrigger>
          <PillTabsTrigger value="expenses"><Receipt className="mr-1.5 h-4 w-4" />Expenses</PillTabsTrigger>
          <PillTabsTrigger value="bookings"><Calendar className="mr-1.5 h-4 w-4" />Bookings</PillTabsTrigger>
        </PillTabsList>
        <TabsContent value="reports" className="mt-4"><ReportsTab /></TabsContent>
        <TabsContent value="accounts" className="mt-4"><AccountsTab /></TabsContent>
        <TabsContent value="ledger" className="mt-4"><LedgerTab /></TabsContent>
        <TabsContent value="expenses" className="mt-4"><ExpensesTab /></TabsContent>
        <TabsContent value="bookings" className="mt-4"><BookingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================ REPORTS ============================ */
function ReportsTab() {
  const { data: lines = [] } = useQuery({
    queryKey: ["erp-report-lines"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("erp_ledger_lines")
        .select("debit_cents,credit_cents,account_id,erp_ledger_entries(status,entry_date)")
        .limit(5000);
      if (error) throw error;
      return (data ?? []) as Array<{ debit_cents: number; credit_cents: number; account_id: string; erp_ledger_entries: { status: string; entry_date: string } | null }>;
    },
  });
  const { data: accounts = [] } = useQuery({
    queryKey: ["erp-accounts-all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("erp_accounts").select("id,code,name,type,currency").order("code");
      if (error) throw error;
      return (data ?? []) as Array<Pick<Account, "id"|"code"|"name"|"type"|"currency">>;
    },
  });
  const { data: expenses = [] } = useQuery({
    queryKey: ["erp-exp-totals"],
    queryFn: async () => {
      const { data, error } = await supabase.from("erp_expenses").select("amount_cents,status,expense_date").limit(2000);
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: bookings = [] } = useQuery({
    queryKey: ["erp-bk-totals"],
    queryFn: async () => {
      const { data, error } = await supabase.from("erp_bookings").select("total_cents,status,start_at").limit(2000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const summary = useMemo(() => {
    const byAccount = new Map<string, number>();
    for (const l of lines) {
      if (l.erp_ledger_entries?.status === "void") continue;
      byAccount.set(l.account_id, (byAccount.get(l.account_id) ?? 0) + l.debit_cents - l.credit_cents);
    }
    let revenue = 0, expenseAcc = 0, assets = 0, liab = 0;
    for (const a of accounts) {
      const bal = byAccount.get(a.id) ?? 0;
      if (a.type === "revenue") revenue += -bal;
      else if (a.type === "expense") expenseAcc += bal;
      else if (a.type === "asset") assets += bal;
      else if (a.type === "liability") liab += -bal;
    }
    const paidExp = expenses.filter((e: any) => e.status === "paid").reduce((s, e: any) => s + e.amount_cents, 0);
    const bookingRev = bookings.filter((b: any) => ["confirmed","checked_in","checked_out"].includes(b.status))
      .reduce((s, b: any) => s + b.total_cents, 0);
    return { revenue, expenseAcc, profit: revenue - expenseAcc, assets, liab, paidExp, bookingRev };
  }, [lines, accounts, expenses, bookings]);

  return (
    <StatsStrip
      tiles={[
        { label: "Revenue (ledger)", value: fmtMoney(summary.revenue), icon: TrendingUp, tone: "success" },
        { label: "Expenses (ledger)", value: fmtMoney(summary.expenseAcc), icon: TrendingDown, tone: "warning" },
        { label: "Net P&L", value: fmtMoney(summary.profit), icon: DollarSign, tone: summary.profit >= 0 ? "success" : "danger" },
        { label: "Cash (assets − liab.)", value: fmtMoney(summary.assets - summary.liab), icon: Wallet, tone: "info" },
        { label: "Paid expenses", value: fmtMoney(summary.paidExp), icon: Receipt },
        { label: "Booking revenue", value: fmtMoney(summary.bookingRev), icon: Calendar, tone: "info" },
        { label: "Accounts", value: accounts.length, icon: Landmark },
        { label: "Ledger entries", value: new Set(lines.map((l: any) => (l as any).entry_id)).size || lines.length, icon: FileText },
      ]}
    />
  );
}


/* ============================ ACCOUNTS ============================ */
function AccountsTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const upsertFn = useServerFn(upsertAccount);
  const delFn = useServerFn(deleteAccount);

  const { data = [] } = useQuery({
    queryKey: ["erp-accounts"],
    queryFn: async () => {
      const { data, error } = await supabase.from("erp_accounts").select("*").order("code");
      if (error) throw error;
      return (data ?? []) as Account[];
    },
  });

  const save = useMutation({
    mutationFn: (input: any) => upsertFn({ data: input }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-accounts"] }); setOpen(false); toast.success("Account saved"); },
    onError: (e: any) => toast.error(e.message ?? "Failed to save"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-accounts"] }); toast.success("Deleted"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  return (
    <div className="space-y-4">
      <AdminPageHeader title="Chart of accounts" description="Define accounts used across the ledger." newLabel="New account"
        onNew={() => { setEditing(null); setOpen(true); }} />
      <div className="rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr><th className="p-2 text-left">Code</th><th className="p-2 text-left">Name</th><th className="p-2 text-left">Type</th><th className="p-2 text-left">Currency</th><th className="p-2 text-left">Active</th><th /></tr>
          </thead>
          <tbody>
            {data.map((a) => (
              <tr key={a.id} className="border-t">
                <td className="p-2 font-mono">{a.code}</td>
                <td className="p-2">{a.name}</td>
                <td className="p-2"><Badge variant="outline">{a.type}</Badge></td>
                <td className="p-2">{a.currency}</td>
                <td className="p-2">{a.active ? "Yes" : "No"}</td>
                <td className="p-2"><RowActions onEdit={() => { setEditing(a); setOpen(true); }} onDelete={() => { if (confirm("Delete account?")) remove.mutate(a.id); }} /></td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No accounts yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <AccountDialog open={open} onOpenChange={setOpen} account={editing} accounts={data} onSave={(p) => save.mutate(p)} />
    </div>
  );
}

function AccountDialog({ open, onOpenChange, account, accounts, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void; account: Account | null; accounts: Account[]; onSave: (p: any) => void;
}) {
  const [form, setForm] = useState<Partial<Account>>(account ?? { type: "asset", currency: "USD", active: true });
  useMemo(() => { setForm(account ?? { type: "asset", currency: "USD", active: true }); }, [account, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{account ? "Edit account" : "New account"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={form.code ?? ""} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
            <div><Label>Currency</Label><Input value={form.currency ?? "USD"} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></div>
          </div>
          <div><Label>Name</Label><Input value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v as AccountType })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ACCOUNT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Parent</Label>
              <Select value={form.parent_id ?? "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? null : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {accounts.filter(a => a.id !== form.id).map(a => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="flex items-center gap-2"><Switch checked={form.active ?? true} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave({ ...form, id: account?.id })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================ LEDGER ============================ */
function LedgerTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const createFn = useServerFn(createJournalEntry);
  const voidFn = useServerFn(voidJournalEntry);

  const { data: accounts = [] } = useQuery({
    queryKey: ["erp-accounts"],
    queryFn: async () => {
      const { data, error } = await supabase.from("erp_accounts").select("*").eq("active", true).order("code");
      if (error) throw error;
      return (data ?? []) as Account[];
    },
  });
  const { data: entries = [] } = useQuery({
    queryKey: ["erp-entries"],
    queryFn: async () => {
      const { data, error } = await supabase.from("erp_ledger_entries").select("*").order("entry_date", { ascending: false }).limit(200);
      if (error) throw error;
      return (data ?? []) as Entry[];
    },
  });
  const { data: lines = [] } = useQuery({
    queryKey: ["erp-lines"],
    queryFn: async () => {
      const { data, error } = await supabase.from("erp_ledger_lines").select("*").limit(2000);
      if (error) throw error;
      return (data ?? []) as Line[];
    },
  });

  const create = useMutation({
    mutationFn: (input: any) => createFn({ data: input }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-entries"] }); qc.invalidateQueries({ queryKey: ["erp-lines"] }); setOpen(false); toast.success("Entry posted"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  const voidIt = useMutation({
    mutationFn: (id: string) => voidFn({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-entries"] }); toast.success("Voided"); },
  });

  const accMap = useMemo(() => new Map(accounts.map(a => [a.id, a])), [accounts]);
  const linesByEntry = useMemo(() => {
    const m = new Map<string, Line[]>();
    for (const l of lines) { const arr = m.get(l.entry_id) ?? []; arr.push(l); m.set(l.entry_id, arr); }
    return m;
  }, [lines]);

  return (
    <div className="space-y-4">
      <AdminPageHeader title="General ledger" description="Double-entry journal." newLabel="New entry"
        onNew={() => setOpen(true)} />
      <div className="space-y-3">
        {entries.map((e) => (
          <Card key={e.id}>
            <CardHeader className="flex flex-row items-start justify-between gap-2 pb-3">
              <div>
                <CardTitle className="text-sm font-medium">
                  <span className="font-mono">{e.entry_number}</span>
                  <span className="ml-2 text-muted-foreground">{e.entry_date}</span>
                </CardTitle>
                {e.description && <p className="text-sm text-muted-foreground mt-1">{e.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={e.status === "void" ? "destructive" : e.status === "posted" ? "default" : "outline"}>{e.status}</Badge>
                {e.status !== "void" && <Button size="sm" variant="ghost" onClick={() => voidIt.mutate(e.id)}>Void</Button>}
              </div>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground"><tr><th className="text-left p-1">Account</th><th className="text-right p-1">Debit</th><th className="text-right p-1">Credit</th><th className="text-left p-1">Memo</th></tr></thead>
                <tbody>
                  {(linesByEntry.get(e.id) ?? []).map(l => (
                    <tr key={l.id} className="border-t">
                      <td className="p-1">{accMap.get(l.account_id) ? `${accMap.get(l.account_id)!.code} — ${accMap.get(l.account_id)!.name}` : l.account_id}</td>
                      <td className="p-1 text-right font-mono">{l.debit_cents ? fmtMoney(l.debit_cents, e.currency) : "—"}</td>
                      <td className="p-1 text-right font-mono">{l.credit_cents ? fmtMoney(l.credit_cents, e.currency) : "—"}</td>
                      <td className="p-1 text-muted-foreground">{l.memo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))}
        {entries.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">No journal entries yet.</p>}
      </div>
      <JournalDialog open={open} onOpenChange={setOpen} accounts={accounts} onSave={(p) => create.mutate(p)} />
    </div>
  );
}

function JournalDialog({ open, onOpenChange, accounts, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void; accounts: Account[]; onSave: (p: any) => void;
}) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [desc, setDesc] = useState("");
  const [ref, setRef] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [lines, setLines] = useState<Array<{ account_id: string; debit: string; credit: string; memo: string }>>([
    { account_id: "", debit: "", credit: "", memo: "" }, { account_id: "", debit: "", credit: "", memo: "" },
  ]);
  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = totalDebit > 0 && Math.abs(totalDebit - totalCredit) < 0.005;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>New journal entry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div><Label>Reference</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} /></div>
            <div><Label>Currency</Label><Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} /></div>
          </div>
          <div><Label>Description</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
          <div className="rounded border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase"><tr><th className="p-2 text-left">Account</th><th className="p-2 text-right">Debit</th><th className="p-2 text-right">Credit</th><th className="p-2 text-left">Memo</th><th /></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1">
                      <Select value={l.account_id} onValueChange={(v) => { const next = [...lines]; next[i].account_id = v; setLines(next); }}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="Select account" /></SelectTrigger>
                        <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1"><Input className="text-right" type="number" step="0.01" value={l.debit} onChange={(e) => { const n = [...lines]; n[i].debit = e.target.value; setLines(n); }} /></td>
                    <td className="p-1"><Input className="text-right" type="number" step="0.01" value={l.credit} onChange={(e) => { const n = [...lines]; n[i].credit = e.target.value; setLines(n); }} /></td>
                    <td className="p-1"><Input value={l.memo} onChange={(e) => { const n = [...lines]; n[i].memo = e.target.value; setLines(n); }} /></td>
                    <td className="p-1"><Button variant="ghost" size="icon" onClick={() => setLines(lines.filter((_, idx) => idx !== i))} disabled={lines.length <= 2}><Trash2 className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-muted/30"><td className="p-2 font-medium">Totals</td>
                  <td className="p-2 text-right font-mono">{totalDebit.toFixed(2)}</td>
                  <td className="p-2 text-right font-mono">{totalCredit.toFixed(2)}</td>
                  <td className="p-2 text-xs" colSpan={2}>{balanced ? <span className="text-emerald-600">Balanced</span> : <span className="text-rose-600">Out of balance</span>}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <Button variant="outline" size="sm" onClick={() => setLines([...lines, { account_id: "", debit: "", credit: "", memo: "" }])}><Plus className="mr-1 h-4 w-4" />Add line</Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!balanced} onClick={() => onSave({
            entry_date: date, description: desc || undefined, reference: ref || undefined, currency,
            lines: lines.filter(l => l.account_id).map(l => ({
              account_id: l.account_id,
              debit_cents: Math.round((parseFloat(l.debit) || 0) * 100),
              credit_cents: Math.round((parseFloat(l.credit) || 0) * 100),
              memo: l.memo || undefined,
            })),
          })}>Post entry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================ EXPENSES ============================ */
function ExpensesTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const upsertFn = useServerFn(upsertExpense);
  const statusFn = useServerFn(setExpenseStatus);
  const delFn = useServerFn(deleteExpense);
  const catFn = useServerFn(upsertExpenseCategory);

  const { data: categories = [] } = useQuery({
    queryKey: ["erp-exp-cats"],
    queryFn: async () => {
      const { data, error } = await supabase.from("erp_expense_categories").select("*").order("name");
      if (error) throw error;
      return (data ?? []) as Category[];
    },
  });
  const { data: expenses = [] } = useQuery({
    queryKey: ["erp-expenses"],
    queryFn: async () => {
      const { data, error } = await supabase.from("erp_expenses").select("*").order("expense_date", { ascending: false }).limit(200);
      if (error) throw error;
      return (data ?? []) as Expense[];
    },
  });

  const save = useMutation({
    mutationFn: (input: any) => upsertFn({ data: input }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-expenses"] }); setOpen(false); toast.success("Expense saved"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: Expense["status"] }) => statusFn({ data: v }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-expenses"] }); toast.success("Status updated"); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-expenses"] }); toast.success("Deleted"); },
  });
  const saveCat = useMutation({
    mutationFn: (input: any) => catFn({ data: input }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-exp-cats"] }); setCatOpen(false); toast.success("Category saved"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">Expenses</h2>
          <p className="text-sm text-muted-foreground">Track outgoing costs with categories and approvals.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCatOpen(true)}>Manage categories</Button>
          <Button onClick={() => { setEditing(null); setOpen(true); }}><Plus className="mr-1 h-4 w-4" />New expense</Button>
        </div>
      </div>
      <div className="rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Vendor</th><th className="p-2 text-left">Category</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">Status</th><th /></tr>
          </thead>
          <tbody>
            {expenses.map(e => {
              const cat = categories.find(c => c.id === e.category_id);
              return (
                <tr key={e.id} className="border-t">
                  <td className="p-2">{e.expense_date}</td>
                  <td className="p-2">{e.vendor || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-2">{cat ? <Badge variant="outline">{cat.name}</Badge> : "—"}</td>
                  <td className="p-2 text-right font-mono">{fmtMoney(e.amount_cents + e.tax_cents, e.currency)}</td>
                  <td className="p-2">
                    <Select value={e.status} onValueChange={(v) => setStatus.mutate({ id: e.id, status: v as Expense["status"] })}>
                      <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>{["draft","submitted","approved","paid","rejected"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-2"><RowActions onEdit={() => { setEditing(e); setOpen(true); }} onDelete={() => { if (confirm("Delete?")) remove.mutate(e.id); }} /></td>
                </tr>
              );
            })}
            {expenses.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No expenses yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <ExpenseDialog open={open} onOpenChange={setOpen} expense={editing} categories={categories} onSave={(p) => save.mutate(p)} />
      <CategoriesDialog open={catOpen} onOpenChange={setCatOpen} categories={categories} onSave={(p) => saveCat.mutate(p)} />
    </div>
  );
}

function ExpenseDialog({ open, onOpenChange, expense, categories, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void; expense: Expense | null; categories: Category[]; onSave: (p: any) => void;
}) {
  const [form, setForm] = useState<any>(() => expense ? {
    ...expense, amount: (expense.amount_cents / 100).toFixed(2), tax: (expense.tax_cents / 100).toFixed(2),
  } : { expense_date: new Date().toISOString().slice(0,10), currency: "USD", status: "submitted", amount: "", tax: "0" });
  useMemo(() => {
    setForm(expense ? { ...expense, amount: (expense.amount_cents/100).toFixed(2), tax: (expense.tax_cents/100).toFixed(2) }
      : { expense_date: new Date().toISOString().slice(0,10), currency: "USD", status: "submitted", amount: "", tax: "0" });
  }, [expense, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{expense ? "Edit expense" : "New expense"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.expense_date ?? ""} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category_id ?? "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? null : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Uncategorized</SelectItem>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Vendor</Label><Input value={form.vendor ?? ""} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>
          <div><Label>Description</Label><Textarea value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div><Label>Tax</Label><Input type="number" step="0.01" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })} /></div>
            <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Payment method</Label><Input value={form.payment_method ?? ""} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["draft","submitted","approved","paid","rejected"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Receipt URL</Label><Input value={form.receipt_url ?? ""} onChange={(e) => setForm({ ...form, receipt_url: e.target.value })} /></div>
          <div><Label>Notes</Label><Textarea value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave({
            id: expense?.id,
            expense_date: form.expense_date,
            category_id: form.category_id ?? null,
            vendor: form.vendor || undefined,
            description: form.description || undefined,
            amount_cents: Math.round((parseFloat(form.amount) || 0) * 100),
            tax_cents: Math.round((parseFloat(form.tax) || 0) * 100),
            currency: form.currency || "USD",
            payment_method: form.payment_method || undefined,
            status: form.status,
            receipt_url: form.receipt_url || undefined,
            notes: form.notes || undefined,
          })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoriesDialog({ open, onOpenChange, categories, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void; categories: Category[]; onSave: (p: any) => void;
}) {
  const [name, setName] = useState(""); const [slug, setSlug] = useState("");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Expense categories</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            {categories.map(c => (
              <div key={c.id} className="flex items-center justify-between rounded border px-3 py-1.5 text-sm">
                <span>{c.name}</span><span className="font-mono text-xs text-muted-foreground">{c.slug}</span>
              </div>
            ))}
            {categories.length === 0 && <p className="text-sm text-muted-foreground">No categories yet.</p>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <Input placeholder="slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button disabled={!name || !slug} onClick={() => { onSave({ name, slug }); setName(""); setSlug(""); }}>Add category</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================ BOOKINGS ============================ */
function BookingsTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Booking | null>(null);
  const upsertFn = useServerFn(upsertBooking);
  const statusFn = useServerFn(setBookingStatus);
  const delFn = useServerFn(deleteBooking);

  const { data = [] } = useQuery({
    queryKey: ["erp-bookings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("erp_bookings").select("*").order("start_at", { ascending: false }).limit(200);
      if (error) throw error;
      return (data ?? []) as Booking[];
    },
  });

  const save = useMutation({
    mutationFn: (input: any) => upsertFn({ data: input }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-bookings"] }); setOpen(false); toast.success("Booking saved"); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: Booking["status"] }) => statusFn({ data: v }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-bookings"] }); toast.success("Updated"); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["erp-bookings"] }); toast.success("Deleted"); },
  });

  return (
    <div className="space-y-4">
      <AdminPageHeader title="Bookings" description="Reservations and scheduled use of resources." newLabel="New booking"
        onNew={() => { setEditing(null); setOpen(true); }} />
      <div className="rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr><th className="p-2 text-left">Ref</th><th className="p-2 text-left">Resource</th><th className="p-2 text-left">Customer</th><th className="p-2 text-left">Start</th><th className="p-2 text-left">End</th><th className="p-2 text-right">Total</th><th className="p-2 text-left">Status</th><th /></tr>
          </thead>
          <tbody>
            {data.map(b => (
              <tr key={b.id} className="border-t">
                <td className="p-2 font-mono text-xs">{b.reference}</td>
                <td className="p-2">{b.resource_name}<div className="text-xs text-muted-foreground">{b.resource_type}</div></td>
                <td className="p-2">{b.customer_name}<div className="text-xs text-muted-foreground">{b.customer_email}</div></td>
                <td className="p-2 text-xs">{new Date(b.start_at).toLocaleString()}</td>
                <td className="p-2 text-xs">{new Date(b.end_at).toLocaleString()}</td>
                <td className="p-2 text-right font-mono">{fmtMoney(b.total_cents, b.currency)}</td>
                <td className="p-2">
                  <Select value={b.status} onValueChange={(v) => setStatus.mutate({ id: b.id, status: v as Booking["status"] })}>
                    <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>{["pending","confirmed","checked_in","checked_out","cancelled","no_show"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-2"><RowActions onEdit={() => { setEditing(b); setOpen(true); }} onDelete={() => { if (confirm("Delete?")) remove.mutate(b.id); }} /></td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No bookings yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <BookingDialog open={open} onOpenChange={setOpen} booking={editing} onSave={(p) => save.mutate(p)} />
    </div>
  );
}

function BookingDialog({ open, onOpenChange, booking, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void; booking: Booking | null; onSave: (p: any) => void;
}) {
  const initial = () => booking ? {
    ...booking,
    start_at: booking.start_at.slice(0, 16),
    end_at: booking.end_at.slice(0, 16),
    total: (booking.total_cents / 100).toFixed(2),
  } : {
    resource_name: "", resource_type: "", customer_name: "", customer_email: "", customer_phone: "",
    start_at: new Date().toISOString().slice(0, 16),
    end_at: new Date(Date.now() + 3600_000).toISOString().slice(0, 16),
    guests: 1, status: "pending", total: "0", currency: "USD", notes: "",
  };
  const [form, setForm] = useState<any>(initial);
  useMemo(() => { setForm(initial()); }, [booking, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{booking ? "Edit booking" : "New booking"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Resource name</Label><Input value={form.resource_name} onChange={(e) => setForm({ ...form, resource_name: e.target.value })} /></div>
            <div><Label>Resource type</Label><Input value={form.resource_type ?? ""} onChange={(e) => setForm({ ...form, resource_type: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Customer name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
            <div><Label>Customer email</Label><Input type="email" value={form.customer_email ?? ""} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Customer phone</Label><Input value={form.customer_phone ?? ""} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
            <div><Label>Guests</Label><Input type="number" min={1} value={form.guests} onChange={(e) => setForm({ ...form, guests: parseInt(e.target.value) || 1 })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start</Label><Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} /></div>
            <div><Label>End</Label><Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Total</Label><Input type="number" step="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} /></div>
            <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["pending","confirmed","checked_in","checked_out","cancelled","no_show"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave({
            id: booking?.id,
            resource_name: form.resource_name,
            resource_type: form.resource_type || undefined,
            customer_name: form.customer_name,
            customer_email: form.customer_email || undefined,
            customer_phone: form.customer_phone || undefined,
            start_at: new Date(form.start_at).toISOString(),
            end_at: new Date(form.end_at).toISOString(),
            guests: form.guests,
            status: form.status,
            total_cents: Math.round((parseFloat(form.total) || 0) * 100),
            currency: form.currency || "USD",
            notes: form.notes || undefined,
          })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
